import { useEffect, useState } from 'react';
import { Container, Title, Text, Card, SimpleGrid, Button, Stack, Alert, Badge, Group, ThemeIcon, Divider } from '@mantine/core';
import { IconBolt, IconCalculator, IconInfoCircle, IconFlame, IconSolarPanel, IconCar, IconCheck, IconX, IconCalendar, IconUsers, IconChartBar, IconSparkles, IconTrash, IconFileDownload, IconFileUpload, IconClockHour4, IconAlertTriangle, IconShieldLock } from '@tabler/icons-react';
import { getCurrentUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../shared/components';
import { getSavedCalculationByUserOSDAndYear, deleteSavedCalculation } from '../../lib/api/savedCalculationApi';
import { getCurrentYearProfessionalCalculation, deleteCurrentYearProfessionalCalculation, type ProfessionalCalculation } from '../../lib/api/professionalCalculationApi';
import { parseSavedProCalculation } from '../../lib/api/savedProCalculationApi';
import { generateProCalculationCSV } from '../../utils/csv/generateProCalculationCSV';
import { downloadCalculationCSV } from '../../utils/csv/downloadCSV';
import { useSEO } from '../../shared/hooks';
import { ExportCSVButton } from '../calculator';
import { useGetImportSummary } from '../../shared/hooks';
import { useGetAvailableYears } from '../hourly-import/hooks/useGetAvailableYears';
import { useOSD } from '../../contexts/OSDContext';
import { getOSDShortName } from '../../utils/osdMetadata';
import { getAmplifyClient } from '../../lib/amplifyClient';
import { isAuthError } from '../../utils/authErrors';
import type { OSDCode } from '../../types/osd.types';

interface SavedCalculationData {
  id: string;
  osd: OSDCode; // OSD (Operator Systemu Dystrybucyjnego)
  calculationYear: number;
  monthlyConsumption: number[];
  householdSize: number;
  hasHeatPump?: boolean;
  hasSolarPanels?: boolean;
  hasElectricVehicle?: boolean;
  hourlyPattern?: any; // JSON { weekday: [24], weekend: [24] }
  calculationResults: {
    recommendedTariff?: string;
    potentialSavings?: number;
    tariffs?: Array<{
      tariffCode: string;
      annualCost: number;
      energyCost: number;
      distributionCost: number;
    }>;
  };
  updatedAt: string;
}

export function Dashboard() {
  useSEO({ title: 'Panel Klienta', noindex: true });
  const navigate = useNavigate();
  const { currentOSD } = useOSD(); // Get current OSD from context
  const [loading, setLoading] = useState(true);
  const [savedCalculation, setSavedCalculation] = useState<SavedCalculationData | null>(null);
  const [professionalCalculation, setProfessionalCalculation] = useState<ProfessionalCalculation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteProCalcLoading, setDeleteProCalcLoading] = useState(false);
  const [deleteSavedCalcLoading, setDeleteSavedCalcLoading] = useState(false);
  const [deleteAllDataLoading, setDeleteAllDataLoading] = useState(false);

  // Load import summary for most recent year with data (S6: fix year default)
  const currentYear = new Date().getFullYear();
  const { yearsWithData } = useGetAvailableYears();
  const importYear = yearsWithData.length > 0 ? Math.max(...yearsWithData) : currentYear;
  const { data: importSummary, loading: importSummaryLoading } = useGetImportSummary(importYear);

  useEffect(() => {
    void loadSavedCalculation();
    void loadProfessionalCalculation();
  }, [currentOSD]); // Reload when OSD changes

  async function loadSavedCalculation() {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      const currentYear = new Date().getFullYear();

      // Multi-OSD support: Load calculation for current OSD only
      if (!currentOSD) {
        setLoading(false);
        return;
      }

      const calculation = await getSavedCalculationByUserOSDAndYear(
        user.userId,
        currentOSD,
        currentYear
      );

      if (calculation) {
        // CRITICAL: Parse JSON fields returned from GraphQL
        const parsedCalculation: SavedCalculationData = {
          id: calculation.id,
          osd: calculation.osd, // Include OSD from database
          calculationYear: calculation.calculationYear,
          monthlyConsumption: typeof calculation.monthlyConsumption === 'string'
            ? JSON.parse(calculation.monthlyConsumption)
            : calculation.monthlyConsumption,
          householdSize: calculation.householdSize,
          hasHeatPump: calculation.hasHeatPump,
          hasSolarPanels: calculation.hasSolarPanels,
          hasElectricVehicle: calculation.hasElectricVehicle,
          hourlyPattern: calculation.hourlyPattern
            ? typeof calculation.hourlyPattern === 'string'
              ? JSON.parse(calculation.hourlyPattern)
              : calculation.hourlyPattern
            : undefined,
          calculationResults: typeof calculation.calculationResults === 'string'
            ? JSON.parse(calculation.calculationResults)
            : calculation.calculationResults,
          updatedAt: calculation.updatedAt,
        };
        setSavedCalculation(parsedCalculation);
      } else {
        // Clear calculation if none exists for current OSD
        setSavedCalculation(null);
      }
    } catch (err) {
      if (isAuthError(err)) {
        setError('Sesja wygasła. Zaloguj się ponownie.');
      } else {
        setError('Nie udało się załadować zapisanych danych');
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadProfessionalCalculation() {
    try {
      const calculation = await getCurrentYearProfessionalCalculation();

      if (calculation) {
        // Parse JSON fields if needed
        const parsedCalculation: ProfessionalCalculation = {
          ...calculation,
          simulationResults: calculation.simulationResults
            ? typeof calculation.simulationResults === 'string'
              ? JSON.parse(calculation.simulationResults)
              : calculation.simulationResults
            : undefined,
        };
        setProfessionalCalculation(parsedCalculation);
      }
    } catch (err) {
      // Don't set error for professional calculation - it's optional
    }
  }

  /**
   * Handle delete Advanced Calculator data
   * Removes saved professional calculation for current year
   */
  const handleDeleteProCalculation = async () => {
    if (!professionalCalculation) {
      return;
    }

    // Confirm deletion
    if (!window.confirm('Czy na pewno chcesz usunąć zapisane dane Kalkulatora Profesjonalnego? Ta operacja jest nieodwracalna.')) {
      return;
    }

    try {
      setDeleteProCalcLoading(true);

      await deleteCurrentYearProfessionalCalculation();

      // Clear state
      setProfessionalCalculation(null);

      // Success feedback
      alert('Dane Kalkulatora Profesjonalnego zostały usunięte.');
    } catch (err) {
      if (isAuthError(err)) {
        alert('Sesja wygasła. Wyloguj się i zaloguj ponownie.');
      } else {
        alert('Wystąpił błąd podczas usuwania danych. Spróbuj ponownie.');
      }
    } finally {
      setDeleteProCalcLoading(false);
    }
  };

  /**
   * Handle delete Basic Calculator data (S1)
   */
  const handleDeleteSavedCalculation = async () => {
    if (!savedCalculation) {
      return;
    }

    if (!window.confirm('Czy na pewno chcesz usunąć zapisane dane Kalkulatora Szczegółowego? Ta operacja jest nieodwracalna.')) {
      return;
    }

    try {
      setDeleteSavedCalcLoading(true);
      await deleteSavedCalculation(savedCalculation.id);
      setSavedCalculation(null);
      alert('Dane Kalkulatora Szczegółowego zostały usunięte.');
    } catch (err) {
      if (isAuthError(err)) {
        alert('Sesja wygasła. Wyloguj się i zaloguj ponownie.');
      } else {
        alert('Wystąpił błąd podczas usuwania danych. Spróbuj ponownie.');
      }
    } finally {
      setDeleteSavedCalcLoading(false);
    }
  };

  /**
   * Handle delete all account data (S7 — GDPR Article 17)
   * Deletes all DynamoDB records. Cognito account is kept so user can re-login.
   */
  const handleDeleteAllData = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć WSZYSTKIE dane konta?\n\nZostaną usunięte:\n- Kalkulacje szczegółowe\n- Kalkulacje profesjonalne\n- Zaimportowane dane godzinowe\n- Profil użytkownika\n\nKonto pozostanie aktywne — będziesz mógł się zalogować i zacząć od nowa.\n\nTa operacja jest NIEODWRACALNA.')) {
      return;
    }

    try {
      setDeleteAllDataLoading(true);
      const client = getAmplifyClient();
      const result = await client.mutations.deleteUserData({
        input: JSON.stringify({ confirmDeletion: true }),
      });

      // AppSync AWSJSON double-encodes: handler JSON.stringify + AppSync JSON wrapping
      let raw: unknown = result.data;
      if (typeof raw === 'string') raw = JSON.parse(raw);
      if (typeof raw === 'string') raw = JSON.parse(raw);
      const parsed = raw as { success?: boolean; message?: string } | null;

      if (parsed?.success) {
        setSavedCalculation(null);
        setProfessionalCalculation(null);
        alert('Wszystkie dane konta zostały usunięte. Twoje konto jest nadal aktywne.');
      } else {
        alert(`Błąd podczas usuwania danych: ${parsed?.message || 'Nieznany błąd'}`);
      }
    } catch (err) {
      if (isAuthError(err)) {
        alert('Sesja wygasła. Wyloguj się i zaloguj ponownie.');
      } else {
        alert('Wystąpił błąd podczas usuwania danych. Spróbuj ponownie.');
      }
    } finally {
      setDeleteAllDataLoading(false);
    }
  };

  /**
   * Handle export Professional Calculator CSV
   * Generates and downloads CSV file with professional calculation data
   */
  const handleExportProCalculationCSV = async () => {
    if (!professionalCalculation) {
      return;
    }

    try {
      // Parse professionalCalculation to formValues and results
      const { formValues, results } = parseSavedProCalculation(professionalCalculation as any);

      // Generate CSV
      const csv = generateProCalculationCSV(formValues, results, undefined);

      // Download CSV
      downloadCalculationCSV(csv, formValues.calculationYear);

      // Success feedback (optional, user will see download)
    } catch (err) {
      alert('Wystąpił błąd podczas eksportowania danych do CSV. Spróbuj ponownie.');
    }
  };

  return (
    <Layout isAuthenticated={true}>
      <Container size="lg" py={40}>
        <Stack gap="xl">
          {/* Welcome Section */}
          <div>
            <Title order={1} mb="sm">
              Panel użytkownika {currentOSD && `(${getOSDShortName(currentOSD)})`}
            </Title>
            <Text c="dimmed" size="lg">
              Porównaj taryfy energetyczne {currentOSD && getOSDShortName(currentOSD)} i znajdź najbardziej opłacalną opcję
            </Text>
          </div>

          {/* Info Alert - No Saved Calculation */}
          {!loading && !savedCalculation && (
            <Alert
              icon={<IconInfoCircle size={20} />}
              title="Rozpocznij oszczędzanie na rachunkach za prąd"
              color="blue"
              variant="light"
            >
              <Stack gap="xs">
                <Text size="sm">
                  Nie masz jeszcze zapisanej kalkulacji dla roku {new Date().getFullYear()}.
                </Text>
                <Text size="sm">
                  Użyj kalkulatora taryf, aby porównać 6 dostępnych taryf energetycznych
                  (G11, G12, G12as, G12w, G13, G13s) i znaleźć najlepszą dla Twojego gospodarstwa domowego.
                </Text>
              </Stack>
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              icon={<IconInfoCircle size={20} />}
              title="Uwaga"
              color="orange"
              variant="light"
            >
              {error}
            </Alert>
          )}


          {/* Calculators Section */}
          {!loading && (
            <Stack gap="lg">
              {/* Basic Calculator Card */}
              <Card shadow="sm" padding="xl" radius="md" withBorder style={{ background: 'linear-gradient(135deg, var(--mantine-color-ecoGreen-0) 0%, var(--mantine-color-teal-0) 100%)' }}>
                <Stack gap="lg">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Group gap="sm" mb="xs">
                        <ThemeIcon size="xl" radius="md" color="green" variant="light">
                          <IconCalculator size={24} />
                        </ThemeIcon>
                        <Title order={3}>Kalkulator Szczegółowy</Title>
                      </Group>
                      <Text c="dimmed" size="sm">
                        Szybkie porównanie taryf energetycznych
                      </Text>
                    </div>
                    {savedCalculation ? (
                      <Group gap="xs">
                        <Badge color="green" size="lg" variant="filled">
                          {savedCalculation.osd}
                        </Badge>
                        <Badge color="green" size="lg" variant="light">
                          ROK {savedCalculation.calculationYear}
                        </Badge>
                      </Group>
                    ) : (
                      <Badge color="green" size="lg" variant="light">
                        BASIC
                      </Badge>
                    )}
                  </Group>

                  {/* Basic Calculator Info */}
                  {savedCalculation ? (
                    <>
                      <Divider />

                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                        {/* Recommended Tariff & Savings */}
                        <Stack gap="xs">
                          <Text size="sm" fw={500} c="dimmed">
                            Ostatnia kalkulacja
                          </Text>
                          <Group gap="md">
                            <div>
                              <Text size="xs" c="dimmed">Rekomendowana taryfa</Text>
                              <Badge color="green" size="lg" variant="filled" mt={4}>
                                {savedCalculation.calculationResults?.recommendedTariff || '-'}
                              </Badge>
                            </div>
                            {savedCalculation.calculationResults?.potentialSavings && savedCalculation.calculationResults.potentialSavings > 0 && (
                              <div>
                                <Text size="xs" c="dimmed">Oszczędności</Text>
                                <Text size="lg" fw={600} c="green" mt={4}>
                                  {Math.round(savedCalculation.calculationResults.potentialSavings)} zł/rok
                                </Text>
                              </div>
                            )}
                          </Group>
                          <Group gap="xs">
                            <IconCalendar size={14} color="var(--mantine-color-gray-6)" />
                            <Text size="xs" c="dimmed">
                              Zaktualizowano: {new Date(savedCalculation.updatedAt).toLocaleString('pl-PL', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Text>
                          </Group>
                        </Stack>

                        {/* Household Details */}
                        <Stack gap="xs">
                          <Group gap="xs">
                            <IconUsers size={18} color="var(--mantine-color-green-6)" />
                            <Text size="sm" fw={500} c="dimmed">
                              Charakterystyka gospodarstwa
                            </Text>
                          </Group>
                        <SimpleGrid cols={2} spacing="md">
                          <div>
                            <Text size="xs" c="dimmed" mb={4}>
                              Liczba osób
                            </Text>
                            <Text size="md" fw={600}>
                              {savedCalculation.householdSize} {savedCalculation.householdSize === 1 ? 'osoba' : savedCalculation.householdSize < 5 ? 'osoby' : 'osób'}
                            </Text>
                          </div>
                          <div>
                            <Text size="xs" c="dimmed" mb={4}>
                              Roczne zużycie
                            </Text>
                            <Text size="md" fw={600}>
                              {Math.round(savedCalculation.monthlyConsumption.reduce((sum, val) => sum + val, 0))} kWh
                            </Text>
                          </div>
                        </SimpleGrid>

                        {/* Household Characteristics Badges */}
                        <Group gap="xs" mt="xs">
                          <Badge
                            color={savedCalculation.hasHeatPump ? 'orange' : 'gray'}
                            variant={savedCalculation.hasHeatPump ? 'filled' : 'light'}
                            size="sm"
                            leftSection={
                              <ThemeIcon size="xs" color="transparent" style={{ marginRight: -4 }}>
                                {savedCalculation.hasHeatPump ? <IconCheck size={10} /> : <IconX size={10} />}
                              </ThemeIcon>
                            }
                          >
                            <Group gap={4}>
                              <IconFlame size={12} />
                              Pompa ciepła
                            </Group>
                          </Badge>
                          <Badge
                            color={savedCalculation.hasSolarPanels ? 'yellow' : 'gray'}
                            variant={savedCalculation.hasSolarPanels ? 'filled' : 'light'}
                            size="sm"
                            leftSection={
                              <ThemeIcon size="xs" color="transparent" style={{ marginRight: -4 }}>
                                {savedCalculation.hasSolarPanels ? <IconCheck size={10} /> : <IconX size={10} />}
                              </ThemeIcon>
                            }
                          >
                            <Group gap={4}>
                              <IconSolarPanel size={12} />
                              Fotowoltaika
                            </Group>
                          </Badge>
                          <Badge
                            color={savedCalculation.hasElectricVehicle ? 'blue' : 'gray'}
                            variant={savedCalculation.hasElectricVehicle ? 'filled' : 'light'}
                            size="sm"
                            leftSection={
                              <ThemeIcon size="xs" color="transparent" style={{ marginRight: -4 }}>
                                {savedCalculation.hasElectricVehicle ? <IconCheck size={10} /> : <IconX size={10} />}
                              </ThemeIcon>
                            }
                          >
                            <Group gap={4}>
                              <IconCar size={12} />
                              Samochód elektryczny
                            </Group>
                          </Badge>
                        </Group>
                      </Stack>
                      </SimpleGrid>

                      <Divider />

                      {/* Action Buttons */}
                      <Group gap="md" grow>
                        <Button
                          variant="filled"
                          color="green"
                          size="md"
                          onClick={() => navigate(`/${currentOSD}/kalkulatory/podstawowy`, { state: { savedCalculation } })}
                          leftSection={<IconCalculator size={18} />}
                        >
                          Zaktualizuj
                        </Button>
                        <ExportCSVButton
                          calculationData={{
                            year: savedCalculation.calculationYear,
                            monthlyConsumption: savedCalculation.monthlyConsumption,
                            hourlyConfig: savedCalculation.hourlyPattern || {
                              weekday: Array(24).fill(1 / 24),
                              weekend: Array(24).fill(1 / 24),
                            },
                          }}
                          variant="outline"
                          size="md"
                        />
                        <Button
                          variant="light"
                          color="red"
                          size="md"
                          onClick={handleDeleteSavedCalculation}
                          loading={deleteSavedCalcLoading}
                          leftSection={<IconTrash size={18} />}
                          title="Usuń zapisane dane"
                        >
                          Wyczyść
                        </Button>
                      </Group>
                    </>
                  ) : (
                    <>
                      <Divider />
                      <Alert icon={<IconInfoCircle size={16} />} color="green" variant="light">
                        <Text size="sm">
                          Nie wykonałeś jeszcze kalkulacji. Porównaj 6 taryf i znajdź najbardziej opłacalną!
                        </Text>
                      </Alert>

                      <Button
                        variant="filled"
                        color="green"
                        size="md"
                        onClick={() => navigate(`/${currentOSD}/kalkulatory/podstawowy`, { state: { savedCalculation } })}
                        leftSection={<IconCalculator size={20} />}
                        fullWidth
                      >
                        Rozpocznij kalkulację
                      </Button>
                    </>
                  )}
                </Stack>
              </Card>

              {/* Warning for non-TAURON users - Pro features only available for TAURON */}
              {currentOSD && currentOSD !== 'TAURON' && (
                <Alert
                  icon={<IconAlertTriangle size={20} />}
                  title="Funkcje zaawansowane dostępne tylko dla Tauron"
                  color="yellow"
                  variant="light"
                >
                  <Text size="sm">
                    Kalkulator Profesjonalny oraz Import danych godzinowych są obecnie dostępne wyłącznie dla klientów Tauron.
                    Pracujemy nad rozszerzeniem tych funkcji dla pozostałych operatorów ({getOSDShortName(currentOSD)}).
                  </Text>
                </Alert>
              )}

              {/* Advanced Calculator Card - TAURON only */}
              {currentOSD === 'TAURON' && (
              <Card shadow="sm" padding="xl" radius="md" withBorder style={{ background: 'linear-gradient(135deg, var(--mantine-color-violet-0) 0%, var(--mantine-color-ecoBlue-0) 100%)' }}>
                <Stack gap="lg">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Group gap="sm" mb="xs">
                        <ThemeIcon size="xl" radius="md" color="violet" variant="light">
                          <IconSparkles size={24} />
                        </ThemeIcon>
                        <Title order={3}>Kalkulator Profesjonalny</Title>
                      </Group>
                      <Text c="dimmed" size="sm">
                        Zaawansowana analiza miesięczna
                      </Text>
                    </div>
                    <Badge color="violet" size="lg" variant="filled">
                      ROK {professionalCalculation?.calculationYear || new Date().getFullYear()}
                    </Badge>
                  </Group>

                  {/* Advanced Calculator Info */}
                  {professionalCalculation ? (
                    <>
                      <Divider />
                      <Stack gap="xs">
                        <Text size="sm" fw={500} c="dimmed">
                          Ostatnia kalkulacja
                        </Text>
                        <Group gap="md">
                          <div>
                            <Text size="xs" c="dimmed">Rekomendowana taryfa</Text>
                            <Badge color="violet" size="lg" variant="filled" mt={4}>
                              {professionalCalculation.recommendedTariff || '-'}
                            </Badge>
                          </div>
                          {professionalCalculation.potentialSavings && (
                            <div>
                              <Text size="xs" c="dimmed">Oszczędności</Text>
                              <Text size="lg" fw={600} c="violet" mt={4}>
                                {Math.round(professionalCalculation.potentialSavings)} zł/rok
                              </Text>
                            </div>
                          )}
                        </Group>
                        <Group gap="xs">
                          <IconCalendar size={14} color="var(--mantine-color-gray-6)" />
                          <Text size="xs" c="dimmed">
                            Zaktualizowano: {new Date(professionalCalculation.updatedAt).toLocaleString('pl-PL', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Text>
                        </Group>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Divider />
                      <Stack gap="xs">
                        <Text size="sm" fw={500} c="dimmed">
                          Funkcje PRO
                        </Text>
                        <Group gap="xs" wrap="nowrap">
                          <IconCheck size={16} color="var(--mantine-color-violet-6)" style={{ flexShrink: 0 }} />
                          <Text size="xs">Analiza miesięczna z importem faktur PDF</Text>
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                          <IconCheck size={16} color="var(--mantine-color-violet-6)" style={{ flexShrink: 0 }} />
                          <Text size="xs">Wzorce godzinowe dostosowane do stylu życia</Text>
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                          <IconCheck size={16} color="var(--mantine-color-violet-6)" style={{ flexShrink: 0 }} />
                          <Text size="xs">Precyzyjne porównanie 6 taryf</Text>
                        </Group>
                      </Stack>
                      <Alert icon={<IconInfoCircle size={16} />} color="violet" variant="light">
                        <Text size="sm">
                          Nie wykonałeś jeszcze analizy PRO. Przejdź do kalkulatora i przeprowadź szczegółową analizę miesięczną!
                        </Text>
                      </Alert>
                    </>
                  )}

                  {/* Action buttons */}
                  {professionalCalculation ? (
                    <Group gap="md" grow>
                      <Button
                        variant="filled"
                        color="violet"
                        size="md"
                        onClick={() => navigate(`/${currentOSD}/kalkulatory/zaawansowany`)}
                        leftSection={<IconSparkles size={20} />}
                      >
                        Zaktualizuj
                      </Button>
                      <Button
                        variant="light"
                        color="green"
                        size="md"
                        onClick={handleExportProCalculationCSV}
                        leftSection={<IconFileDownload size={18} />}
                        title="Pobierz dane CSV"
                      >
                        Pobierz dane CSV
                      </Button>
                      <Button
                        variant="light"
                        color="red"
                        size="md"
                        onClick={handleDeleteProCalculation}
                        loading={deleteProCalcLoading}
                        leftSection={<IconTrash size={18} />}
                        title="Usuń zapisane dane"
                      >
                        Wyczyść
                      </Button>
                    </Group>
                  ) : (
                    <Button
                      variant="filled"
                      color="violet"
                      size="md"
                      onClick={() => navigate('/pro-calculator')}
                      leftSection={<IconSparkles size={20} />}
                      fullWidth
                    >
                      Rozpocznij analizę PRO
                    </Button>
                  )}
                </Stack>
              </Card>
              )}

              {/* Hourly Data Import Card - TAURON only */}
              {currentOSD === 'TAURON' && (
              <Card shadow="sm" padding="xl" radius="md" withBorder style={{ background: 'linear-gradient(135deg, var(--mantine-color-ecoAmber-0) 0%, var(--mantine-color-yellow-0) 100%)' }}>
                <Stack gap="lg">
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Group gap="sm" mb="xs">
                        <ThemeIcon size="xl" radius="md" color="orange" variant="light">
                          <IconFileUpload size={24} />
                        </ThemeIcon>
                        <Title order={3}>Import danych godzinowych</Title>
                      </Group>
                      <Text c="dimmed" size="sm">
                        Załaduj dane z Tauron eLicznik
                      </Text>
                    </div>
                    <Badge color="orange" size="lg" variant="filled">
                      NOWE
                    </Badge>
                  </Group>

                  <Divider />

                  <Stack gap="xs">
                    <Text size="sm" fw={500} c="dimmed">
                      Co możesz zrobić?
                    </Text>
                    <Group gap="xs" wrap="nowrap">
                      <IconCheck size={16} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
                      <Text size="xs">Importuj plik CSV z raportu godzinowego Tauron</Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <IconClockHour4 size={16} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
                      <Text size="xs">Automatyczne oszacowanie brakujących miesięcy</Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <IconChartBar size={16} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
                      <Text size="xs">Precyzyjne dane godzinowe dla dokładniejszych kalkulacji</Text>
                    </Group>
                  </Stack>

                  <Alert icon={<IconInfoCircle size={16} />} color="orange" variant="light">
                    <Text size="sm">
                      Import pliku CSV z Tauron eLicznik pozwala na jeszcze bardziej precyzyjne porównanie taryf
                      na podstawie Twoich rzeczywistych danych zużycia energii.
                    </Text>
                  </Alert>

                  <Button
                    variant="filled"
                    color="orange"
                    size="md"
                    onClick={() => navigate('/konto/import')}
                    leftSection={<IconFileUpload size={20} />}
                    fullWidth
                  >
                    Przejdź do importu danych
                  </Button>
                </Stack>
              </Card>
              )}
            </Stack>
          )}

          {/* Imported Data Summary Section */}
          {!loading && !importSummaryLoading && importSummary && importSummary.totalMonths > 0 && (
            <Card shadow="sm" padding="xl" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Group gap="sm" mb="xs">
                      <ThemeIcon size="xl" radius="md" color="green" variant="light">
                        <IconCheck size={24} />
                      </ThemeIcon>
                      <Title order={3}>Zaimportowane dane godzinowe</Title>
                    </Group>
                    <Text c="dimmed" size="sm">
                      Rok {importSummary.year} - {importSummary.totalMonths} {importSummary.totalMonths === 1 ? 'miesiąc' : importSummary.totalMonths <= 4 ? 'miesiące' : 'miesięcy'}
                    </Text>
                  </div>
                  <Badge color="green" size="lg" variant="filled">
                    AKTYWNE
                  </Badge>
                </Group>

                <Divider />

                {importSummary.imports && importSummary.imports.length > 0 && (
                  <Stack gap="xs">
                    <Text size="sm" fw={500} c="dimmed">
                      Zaimportowane miesiące:
                    </Text>
                    <SimpleGrid cols={4} spacing="xs">
                      {importSummary.imports.map((imp) => (
                        <Card key={imp.yearMonth} padding="xs" withBorder>
                          <Group gap="xs" wrap="nowrap">
                            <ThemeIcon
                              size="sm"
                              radius="xl"
                              color={imp.isExtrapolated ? 'orange' : 'green'}
                              variant="light"
                            >
                              {imp.isExtrapolated ? <IconClockHour4 size={12} /> : <IconCheck size={12} />}
                            </ThemeIcon>
                            <Text size="xs" fw={500}>
                              {imp.yearMonth}
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed" mt="xs">
                            {imp.hoursCount} godz.
                          </Text>
                        </Card>
                      ))}
                    </SimpleGrid>
                    <Text size="xs" c="dimmed" mt="sm">
                      <Group gap="xs">
                        <IconCheck size={12} style={{ color: 'var(--mantine-color-green-6)' }} />
                        <span>Dane rzeczywiste</span>
                      </Group>
                      <Group gap="xs">
                        <IconClockHour4 size={12} style={{ color: 'var(--mantine-color-orange-6)' }} />
                        <span>Oszacowane</span>
                      </Group>
                    </Text>
                  </Stack>
                )}

                <Button
                  variant="outline"
                  color="green"
                  size="md"
                  onClick={() => navigate('/hourly-import')}
                  leftSection={<IconFileUpload size={20} />}
                  fullWidth
                >
                  Zarządzaj danymi
                </Button>
              </Stack>
            </Card>
          )}

          {/* GDPR: Delete All Data (S7) */}
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Stack gap="md">
              <Group gap="sm">
                <ThemeIcon size="xl" radius="md" color="red" variant="light">
                  <IconShieldLock size={24} />
                </ThemeIcon>
                <div>
                  <Title order={3}>Zarządzanie danymi konta</Title>
                  <Text c="dimmed" size="sm">Prawo do usunięcia danych (RODO Art. 17)</Text>
                </div>
              </Group>
              <Alert icon={<IconInfoCircle size={16} />} color="orange" variant="light">
                <Text size="sm">
                  Usunięcie danych jest <strong>nieodwracalne</strong>. Twoje konto pozostanie aktywne —
                  możesz się zalogować ponownie i zacząć od nowa.
                </Text>
              </Alert>
              <Button
                variant="light"
                color="red"
                size="md"
                onClick={handleDeleteAllData}
                loading={deleteAllDataLoading}
                leftSection={<IconTrash size={18} />}
              >
                Usuń wszystkie dane konta
              </Button>
            </Stack>
          </Card>

          {/* Quick Actions */}
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Title order={3} mb="md">
              Szybkie akcje
            </Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <Button
                variant="light"
                color="ecoGreen"
                size="lg"
                leftSection={<IconChartBar size={20} />}
                onClick={() => navigate(`/${currentOSD}/kalkulatory/podstawowy`)}
              >
                Porównaj taryfy
              </Button>
              <Button
                variant="outline"
                color="gray"
                size="lg"
                leftSection={<IconBolt size={20} />}
                onClick={() => navigate('/tariffs')}
              >
                Zobacz informacje o taryfach
              </Button>
              {savedCalculation && (
                <ExportCSVButton
                  calculationData={{
                    year: savedCalculation.calculationYear,
                    monthlyConsumption: savedCalculation.monthlyConsumption,
                    hourlyConfig: savedCalculation.hourlyPattern || {
                      weekday: Array(24).fill(1 / 24),
                      weekend: Array(24).fill(1 / 24),
                    },
                  }}
                  variant="outline"
                  size="lg"
                  fullWidth
                  location="dashboard_quick_actions"
                />
              )}
            </SimpleGrid>
          </Card>
        </Stack>
      </Container>
    </Layout>
  );
}
